
import { FavouriteItem } from '../types-store';
import {
  AllowedFavouriteType, DisabledWarningType, FolderFavouriteType, scheduleCompanyReports, SharedWithPerFavouriteType
} from 'app/area/favourite-administration/form/constants';
import { FavouriteAdminTranslation, maxFavoriteIdForOwner, maxFavouriteId } from 'app/area/favourite-administration/constants';
import { isDisabled } from 'app/infrastructure/data/date';
import { routeConstants } from 'app/routes/route-constants';
import { ValidationLevel, ValidationProps } from 'ui';
import { toAbsoluteUrl } from 'app/infrastructure/hosting';

export const canDelete = (id: number) => id >= maxFavouriteId;

export const favouriteTypeReadOnly = (id: number) =>
  !Object.values(AllowedFavouriteType).includes(id);

export const isOwnerReadOnly = (id: number) => id < maxFavoriteIdForOwner;

export const iSharedWithVisibleAndEditable = (id: number) =>
  !!id && !Object.values(SharedWithPerFavouriteType).includes(id);

export const displayScheduleSection = (reportTypeValue: number) =>
  reportTypeValue === scheduleCompanyReports;

export const displayRecipientsSection = (favouriteItem: FavouriteItem) => !!favouriteItem.flgEmail;

export const getFavouriteName = (favouriteItem: FavouriteItem, mainLanguage: number) =>
  favouriteItem.language
    ?.find(languageItem => languageItem.value === mainLanguage)
    ?.description;

export const disabledWarning = (id: number, disabled: Date) =>
  isDisabled(disabled) && Object.values(DisabledWarningType).includes(id);

export const isCodeExtra = (id: number) => [
  FolderFavouriteType.savedReports,
  FolderFavouriteType.savedCompanyReports,
  FolderFavouriteType.savedDashboards
].some(key => key === id);

export const favouriteLinkWithParams = (id: string, params: number[] = []) =>
  params.length > 0
    ? toAbsoluteUrl(routeConstants.favouriteWithParams(`${id}=${params.join(',')}`))
    : undefined;

export const ownerValidation = (
  favouriteItem: FavouriteItem,
  originalFavouriteItem: FavouriteItem,
  validation: ValidationProps,
  translation: FavouriteAdminTranslation
): ValidationProps => {
  const ownerDisabled = isDisabled(favouriteItem?.owner?.disabled);
  const ownerInvalid = (favouriteItem?.invalidOwner
    && favouriteItem?.owner?.value === originalFavouriteItem?.owner?.value);

  return {
    ...(ownerDisabled || ownerInvalid) ? {
      level: ValidationLevel.warning, messages: [
        ownerDisabled && {
          level: ValidationLevel.warning,
          message: translation.disabled_user_selected
        },
        ownerInvalid && {
          level: ValidationLevel.warning,
          message: translation.invalid_owner_warning
        }
      ].filter(message => !!message)
    } : undefined,
    ...validation
  };
};

export const parentValidation = (
  favouriteItem: FavouriteItem,
  validation: ValidationProps,
  translation: FavouriteAdminTranslation
): ValidationProps => ({
  ...validation,
  ...(isDisabled(favouriteItem?.parent?.disabled) ?
    {
      level: ValidationLevel.warning, messages: [{
        level: ValidationLevel.warning,
        message: translation.disabled_folder_selected
      }]
    } : undefined),
  ...(favouriteItem?.invalidOwnerInPath ?
    {
      level: ValidationLevel.warning, messages: [{
        level: ValidationLevel.warning,
        message: translation.folder_wrong_owner_warning
      }]
    } : undefined)
});

export const disabledValidation = (
  isParentDisabled: boolean,
  showDisabledWarning: boolean,
  validation: ValidationProps,
  translation: FavouriteAdminTranslation
): ValidationProps => ({
  ...validation,
  ...(
    (showDisabledWarning || isParentDisabled) ?
      {
        level: ValidationLevel.warning, messages: [{
          level: ValidationLevel.warning,
          message: isParentDisabled
            ? translation.disabled_warning_disabled_folder
            : translation.disabled_warning
        }]
      } : undefined
  )
});
